import React, { useState } from 'react';
import { history, useIntl } from 'umi';
import { notification } from 'tea-component';
import { fetchDBIAIPageAccessData } from '@home/services/search';
import { isEmpty } from '@intelligence/v3/utils/common';
import { buildFilters } from '@intelligence/v3/utils/aiNavigate';
import { isExternalUrl, buildExternalUrl, openInNewTab } from '../utils';
import { logIntelligencePageAIAccess } from '../utils/log';
import { getAISearchPageSupportResult, normalizeAISearchSystemCode } from '../utils/pageSupport';
import {
  validateList,
  analysisCompany,
  analysisDateRange,
  analysisGranularityDateRange,
  analysisCountry,
  analysisGenre,
  analysisSensortowerTags,
  analysisAppmagicTags,
  analysisMinMaxRange,
  analysisGrowth,
  analysisGameEngine,
  analysisGSDPublisherShared,
  analysisSteamTags,
  analysisKpiMetrics,
} from '../utils/formatters';
import { DBSystemCode } from '@home/_config/config';
import { DBO_U } from '@op/utils/format';

import type { TDBSystemCode } from '@home/types/aiSearch';
import type { IFetchDBIAIPageAccessDataParams } from '@home/services/search';

interface IAISearchNavigationSuccessParams {
  keyword: string;
  system?: TDBSystemCode;
  pathname: string;
}

export default function useIntelligencePageAIAccess(props: {
  onNavigation?: () => void;
  onBackClick?: () => void;
  onSuccess?: (params: IAISearchNavigationSuccessParams) => void;
}) {
  const { onNavigation, onBackClick, onSuccess } = props;

  const { formatMessage } = useIntl();

  // AI Page Navigation数据 加载状态
  const [aiNavigationLoading, setAiNavigationLoading] = useState(false);
  // AI Page Navigation数据 加载异常
  const [aiNavigationError, setAiNavigationError] = useState(false);

  // 解析agent侧提供的filters
  const analysisFilters = async (filters: IRecord): Promise<IRecord> => {
    if (!filters) {
      return {};
    }

    const { ...restFilters } = filters;

    const dateRangeFilters = analysisGranularityDateRange(filters);

    const genreFilters = analysisGenre(filters);

    const gameFilters = validateList('game_ids', filters);

    const releaseDateFilters = analysisDateRange('daily', 'release_start_date', 'release_end_date', filters);

    // 并行执行 region 和 company 的异步查询，且互不影响
    const [regionFilters, companyFilters] = await Promise.all([analysisCountry(filters), analysisCompany(filters)]);

    const sensortowerTagFilters = analysisSensortowerTags(filters);

    const appmagicTagFilters = analysisAppmagicTags(filters);

    const minMaxRangeFilters = analysisMinMaxRange(filters);

    const growthFilters = analysisGrowth(['revenue'], filters);

    const gameEngineFilters = analysisGameEngine(filters);

    const dataSourceFilters = validateList('dataSource', filters);

    const platformFilters = validateList('platform', filters);

    const cascaderPlatformFilters = validateList('cascader_platform', filters);

    // 新增：GSD Publisher Shared 筛选器
    const gsdPublisherSharedFilters = analysisGSDPublisherShared(filters);

    // 新增：Steam Tags 筛选器
    const steamTagsFilters = analysisSteamTags(filters);

    // 新增：Game Mode 筛选器
    const gameModeFilters = validateList('game_mode', filters);

    // 新增：Segment for Sales 筛选器
    const segmentFilters = validateList('cascader_segment', filters);

    // 新增：KPI 指标筛选器
    const kpiMetricsFilters = analysisKpiMetrics(filters);

    // 新增：Retention 筛选器
    const retentionFilters = validateList('retention', filters);

    return {
      ...restFilters,
      ...dateRangeFilters,
      ...regionFilters,
      ...genreFilters,
      ...gameFilters,
      ...companyFilters,
      ...releaseDateFilters,
      ...sensortowerTagFilters,
      ...appmagicTagFilters,
      ...minMaxRangeFilters,
      ...growthFilters,
      ...gameEngineFilters,
      ...dataSourceFilters,
      ...platformFilters,
      ...cascaderPlatformFilters,
      ...gsdPublisherSharedFilters,
      ...steamTagsFilters,
      ...gameModeFilters,
      ...segmentFilters,
      ...kpiMetricsFilters,
      ...retentionFilters,
    };
  };

  /** 各系统导航策略 */
  type NavigateFn = (pathname: string, filters: IRecord, resData: IRecord) => Promise<IRecord>;

  /** 默认导航：URL query string 方式跳转 */
  const defaultNavigate: NavigateFn = async (pathname, filters) => {
    const separator = pathname.includes('?') ? '&' : '?';
    const url = `${pathname}${separator}${DBO_U.objToUrl(filters)}`;
    history.push(url);
    return {};
  };

  const navigators: Partial<Record<TDBSystemCode, NavigateFn>> = {
    [DBSystemCode.Opinions]: defaultNavigate,
    [DBSystemCode.Intelligence]: async (pathname, filters) => {
      let intelligenceFilters: IRecord = {};
      try {
        intelligenceFilters = await analysisFilters(filters);
      } catch (error) {
        console.error(error);
        intelligenceFilters = {};
      }
      const query = isEmpty(intelligenceFilters) ? {} : { ...intelligenceFilters, use_url_filter: '1' };
      history.push({ pathname, query });
      return query;
    },
  };

  /**
   * 情报页面AI直达
   * @param params - 参数对象
   */
  const handleIntelligencePageAIAccessClick = async (params: IFetchDBIAIPageAccessDataParams) => {
    // AI 页面导航
    setAiNavigationLoading(true);
    setAiNavigationError(false);

    let res: any = {};
    let query: IRecord = {};

    try {
      res = await fetchDBIAIPageAccessData(params);
      const resData = res?.data || {};
      const navigationData = resData?.navigate || {};
      const url = navigationData?.base_url?.replace(/^(\/)?v2/, '') || '';
      const { url: baseUrl, filters } = buildFilters(url, navigationData?.filters);
      const rawSystem = navigationData?.system || '';
      const pageSupportResult = getAISearchPageSupportResult({
        system: rawSystem,
        url: baseUrl,
      });
      const targetSystem = pageSupportResult.system || normalizeAISearchSystemCode(rawSystem);

      // 判断是否为外部链接（第三方站点）
      const isExternal = isExternalUrl(baseUrl);

      if (res?.code === 0 && baseUrl) {
        if (isExternal) {
          // 外部链接：在新标签页打开
          const externalUrl = buildExternalUrl(baseUrl, filters);
          const flag = openInNewTab(externalUrl);
          if (!flag) {
            throw new Error(`跳转失败: ${JSON.stringify(res)}`);
          }
        } else {
          // 内部路由：使用 history.push
          const navigate = (targetSystem && navigators[targetSystem]) || defaultNavigate;
          query = await navigate(baseUrl, filters, resData);
        }
        // 显示成功提示
        notification.success({
          unique: true,
          description: formatMessage({
            id: pageSupportResult.supportsFilters
              ? 'dbh_ai_search_page_navigated'
              : 'dbh_ai_search_page_navigated_without_filters',
          }),
        });
        // 打点
        logIntelligencePageAIAccess({
          success: true,
          keyword: params?.keyword || '',
          agent_res: res,
          intelligence_page_path: baseUrl,
          intelligence_page_query: isExternal ? filters : query,
        });
        onSuccess?.({
          keyword: params?.keyword || '',
          system: targetSystem,
          pathname: pageSupportResult.pathname,
        });
        onNavigation?.();
      } else {
        throw new Error(`跳转失败: ${JSON.stringify(res)}`);
      }
    } catch (error) {
      console.error(error);
      // 打点、上报aegis
      logIntelligencePageAIAccess({
        success: false,
        keyword: params?.keyword || '',
        agent_res: res,
        intelligence_page_path: '',
        intelligence_page_query: {},
      });
      // 未找到页面，显示错误提示
      setAiNavigationError(true);
    } finally {
      setAiNavigationLoading(false);
    }
  };

  /**
   * 点击"回到搜索"按钮
   * @param e - 点击事件对象
   */
  const handleIntelligencePageAIAccessBackClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    resetIntelligencePageAIAccessError();
  };

  const resetIntelligencePageAIAccessError = () => {
    onBackClick?.();

    // 需要放在定时器里，要先等handleClickOutside事件处理完毕，再切换回搜索初始状态页面，否则handleClickOutside事件处理内会判断错误，从而导致关闭了搜索下拉弹出面板
    setTimeout(() => {
      setAiNavigationError(false);
    }, 0);
  };

  return {
    aiNavigationLoading,
    aiNavigationError,
    onIntelligencePageAIAccessClick: handleIntelligencePageAIAccessClick,
    onIntelligencePageAIAccessBackClick: handleIntelligencePageAIAccessBackClick,
    resetIntelligencePageAIAccessError,
  };
}
